/**
 * The airport's money, as the executive modules read it.
 *
 * The platform holds no ledger. What it holds is what work cost, what
 * deferral costs, and what the capital projects have spent; the revenue, the
 * operating cost and the plan come from the finance system, entered here
 * and kept as a dated, attributed note. Until they are entered the modules
 * run on the demonstration's figures and say so on every tile — a number
 * that looks like the airport's when it is not is the failure this file
 * exists to prevent.
 */
import type { SourceRef } from "./decision";
import { t } from "../i18n";

export interface FinanceLine {
  key: string;
  label: string;
  actual: number;
  budget: number;
}

export interface CapexLine {
  id: string;
  name: string;
  budget: number;
  spent: number;
  pctComplete: number;
}

export interface MonthLine {
  /** YYYY-MM */
  month: string;
  revenue: number;
  cost: number;
  budget: number;
}

export interface Finance {
  currency: string;
  /** what the year-to-date figures cover */
  period: string;
  /** days the period covers, for the daily figures */
  daysElapsed: number;
  /** passengers in the same period, for the per-passenger figures */
  passengers?: number;
  revenue: FinanceLine[];
  opex: FinanceLine;
  /** the operating cost by area, when the ledger's split is known */
  opexByArea?: FinanceLine[];
  capex: CapexLine[];
  monthly: MonthLine[];
  cash?: { balance?: number; facility?: number };
  /** where the figures came from */
  basis: "demo" | "entered" | "ledger";
  enteredBy?: string;
  enteredAt?: string;
  /** set when the figures were read from a finance export rather than typed */
  ledger?: {
    file: string;
    importedBy: string;
    importedAt: string;
    rows: number;
    mapped: number;
    unmapped: number;
    rejected: number;
    period: string;
  };
}

/** The demonstration's finance feed, as one starting set. Every module marks it. */
export function demoFinance(): Finance {
  return {
    currency: "SAR",
    period: "2026 year to date, January to August",
    daysElapsed: 243,
    passengers: 6_120_000,
    revenue: [
      { key: "aeronautical", label: "Aeronautical", actual: 412_600_000, budget: 398_000_000 },
      { key: "retail", label: "Retail", actual: 98_200_000, budget: 106_000_000 },
      { key: "parking", label: "Parking", actual: 46_100_000, budget: 49_000_000 },
      { key: "concessions", label: "Concessions", actual: 71_500_000, budget: 77_000_000 },
      { key: "advertising", label: "Advertising", actual: 20_600_000, budget: 22_000_000 },
    ],
    opex: { key: "opex", label: "Operating cost", actual: 389_700_000, budget: 372_000_000 },
    opexByArea: [
      { key: "staff", label: "Staff", actual: 163_700_000, budget: 158_100_000 },
      { key: "maintenance", label: "Maintenance and contracts", actual: 70_100_000, budget: 63_200_000 },
      { key: "energy", label: "Energy and utilities", actual: 35_100_000, budget: 31_600_000 },
      { key: "security", label: "Security and services", actual: 54_600_000, budget: 52_100_000 },
      { key: "other", label: "Other", actual: 66_200_000, budget: 67_000_000 },
    ],
    capex: [
      { id: "C-01", name: "Runway 17/35 rehabilitation", budget: 96_000_000, spent: 41_000_000, pctComplete: 38 },
      { id: "C-02", name: "Terminal cooling replacement", budget: 38_000_000, spent: 29_000_000, pctComplete: 61 },
      { id: "C-03", name: "AGL regulator renewal", budget: 14_000_000, spent: 5_200_000, pctComplete: 44 },
      { id: "C-04", name: "Baggage line 3 upgrade", budget: 22_000_000, spent: 18_400_000, pctComplete: 52 },
      { id: "C-05", name: "Apron expansion phase 2", budget: 145_000_000, spent: 12_000_000, pctComplete: 9 },
    ],
    monthly: [
      { month: "2026-01", revenue: 78_100_000, cost: 47_200_000, budget: 76_000_000 },
      { month: "2026-02", revenue: 71_400_000, cost: 45_900_000, budget: 72_000_000 },
      { month: "2026-03", revenue: 84_600_000, cost: 49_800_000, budget: 80_000_000 },
      { month: "2026-04", revenue: 81_200_000, cost: 48_600_000, budget: 81_000_000 },
      { month: "2026-05", revenue: 86_900_000, cost: 50_400_000, budget: 83_000_000 },
      { month: "2026-06", revenue: 79_500_000, cost: 49_100_000, budget: 82_000_000 },
      { month: "2026-07", revenue: 88_300_000, cost: 51_700_000, budget: 85_000_000 },
      { month: "2026-08", revenue: 79_000_000, cost: 47_000_000, budget: 83_000_000 },
    ],
    /*
     * A cash position, so the prolonged-disruption desk can carry a date.
     *
     * Without a balance and a facility every posture on that desk reads "no
     * cash position" and the one figure it exists to produce — the day the
     * money runs out at each level of shutdown — cannot be computed at all.
     * Demonstration figures like everything else here, and marked as such by
     * `basis` below.
     */
    cash: { balance: 180_000_000, facility: 120_000_000 },
    basis: "demo",
  };
}

export const revenueTotal = (f: Finance) => f.revenue.reduce((t, l) => t + l.actual, 0);
export const revenueBudget = (f: Finance) => f.revenue.reduce((t, l) => t + l.budget, 0);
export const commercial = (f: Finance) => f.revenue.filter((l) => l.key !== "aeronautical");

export function financeRef(f: Finance, what: string): SourceRef {
  if (f.basis === "demo")
    return {
      kind: "demo",
      ref: "demo finance feed",
      label: t("{what} — demonstration figures, not the airport's", { what }),
    };
  if (f.basis === "ledger" && f.ledger)
    return {
      kind: "finance",
      ref: "ledger",
      label: t("{what} — from {file}, {period}", { what, file: f.ledger.file, period: f.ledger.period }),
      enteredBy: f.ledger.importedBy,
      enteredAt: f.ledger.importedAt,
    };
  return { kind: "finance", ref: "finance", label: what, enteredBy: f.enteredBy, enteredAt: f.enteredAt };
}

/** How the figures should be described wherever a desk names their origin. */
export function financeOrigin(f: Finance): string {
  if (f.basis === "demo") return t("demonstration figures until the finance system's are connected");
  if (f.basis === "ledger" && f.ledger)
    return t("read from {file} by {who} on {date} — {period}", {
      file: f.ledger.file,
      who: f.ledger.importedBy,
      date: f.ledger.importedAt.slice(0, 10),
      period: f.ledger.period,
    });
  return f.enteredAt
    ? t("entered by {who} on {date}", { who: f.enteredBy ?? "—", date: f.enteredAt.slice(0, 10) })
    : t("entered by {who}", { who: f.enteredBy ?? "—" });
}
